// useAppEffects.ts - with Chrome extension support

import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserResponse } from 'types/api/user';
import { getUrlParams, isInIframe } from 'utils/proxyUtils';
import { store } from '../store';
import { isReady } from '../store/bootstrapSlice';
import { getSelectedSiteCollaborateInfo, setShowSiteSelectionModal } from '../store/siteInfoSlice';
import { setCreateModalOpen, setSelectedTaskId } from '../store/tasksSlice';
import { getDisplayMode, setBrowseMode } from '../store/uiSlice';
import { getAutheticationFailed, getShowForgotPassword, getShowLogin, getShowRegister, getUser, setLoggedIn, setOpenGodaddyGuestNameModal, setShowCompanyDetail, setShowLogin, setShowRegister, setShowUpdateUserName, setSignupOption, setUserDetails } from '../store/userSlice';
import { usePluginActivationRedirect } from './use-activate-plugin';
import { enterpriseClients } from './use-enterprise-account';
import { useUpdateProfile } from './use-update-profile';

// Helper to check if we're in Chrome extension context
const isChromeExtension = () => {
    return typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.id;
};

export const useAppEffects = () => {
    const dispatch = useDispatch();
    const ready = useSelector(isReady);
    const userData = useSelector(getUser);
    const displayMode = useSelector(getDisplayMode);
    const collaborateInfo = useSelector(getSelectedSiteCollaborateInfo)
    const login = useSelector(getShowLogin)
    const register = useSelector(getShowRegister)
    const forgotPassword = useSelector(getShowForgotPassword)
    const isAuthenticationFailed = useSelector(getAutheticationFailed);
    const previousTaskIdRef = useRef<string | null>(null);
    const extensionConnectedRef = useRef(false);
    const { handleUpdateUserProfile } = useUpdateProfile()
    const { handlePluginActivation } = usePluginActivationRedirect()

    // Listen for ESC key press to enable browse mode
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                dispatch(setBrowseMode(true));
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        // Clean up
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [dispatch]);

    // Prevent host-page keyboard listeners from firing for events originating inside our UI
    // while not in browse mode. Attach to the shadow host in bubble phase so our UI still
    // receives target/capture events, and we only block propagation to the host page.
    useEffect(() => {
        const container = document.getElementById('root-atarim');
        if (!container) return;

        const stopPageKeyboardHandlers = (event: KeyboardEvent) => {
            const state = store.getState();
            if (state.ui.browseMode) return;

            // Event originated within our shadow DOM; stop it from leaving the host
            event.stopPropagation();
        };

        container.addEventListener('keydown', stopPageKeyboardHandlers);
        container.addEventListener('keyup', stopPageKeyboardHandlers);
        container.addEventListener('keypress', stopPageKeyboardHandlers);

        return () => {
            container.removeEventListener('keydown', stopPageKeyboardHandlers);
            container.removeEventListener('keyup', stopPageKeyboardHandlers);
            container.removeEventListener('keypress', stopPageKeyboardHandlers);
        };
    }, []);

    // Shadow root ready effect
    useEffect(() => {
        if (!ready && isInIframe()) return;
        window.parent.postMessage({
            type: 'SHADOW_ROOT_READY',
            status: 'ready'
        }, '*');

        // Chrome extension specific - notify the extension that we're ready
        if (isChromeExtension()) {
            try {
                chrome.runtime.sendMessage({
                    type: 'CONTENT_READY',
                    isIframe: isInIframe(),
                    url: window.location.href
                });
            } catch (error) {
                console.error('Failed to send ready message to extension:', error);
            }
        }
    }, [ready]);

    // Listen for messages from parent
    useEffect(() => {
        if (isInIframe()) {
            const handleMessageFromParent = (event: MessageEvent) => {
                if (event.source === window.parent) {
                    if (event.data.type === 'UPDATE_TASK_ID') {
                        const newUrl = new URL(window.location.href);
                        newUrl.searchParams.set('taskid', event.data.taskId);
                        window.history.replaceState(
                            { taskId: event.data.taskId },
                            '',
                            newUrl.toString()
                        );
                        dispatch(setSelectedTaskId(event.data.taskId));
                    }
                }
            };

            window.addEventListener('message', handleMessageFromParent);
            return () => window.removeEventListener('message', handleMessageFromParent);
        } else {
            const taskId = new URL(window.location.toString()).searchParams.get("taskId")
            dispatch(setSelectedTaskId(taskId));
        }
    }, [dispatch]);

    useEffect(() => {
        const searchParams = getUrlParams();
        if (userData?.loggedIn && isInIframe() && (searchParams.has('new'))) {
            window.parent.postMessage({
                type: 'REMOVE_NEW_AND_TASKID_PARAMS'
            }, '*');

            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('new');
            newUrl.searchParams.delete('taskid');
            window.history.replaceState(
                { taskId: null },
                '',
                newUrl.toString()
            );
            dispatch(setSelectedTaskId(null));
            dispatch(setCreateModalOpen(false));
        }
    }, [dispatch, userData]);

    // Chrome extension specific - listen for extension messages
    useEffect(() => {
        if (!isChromeExtension()) return;

        const handleExtensionMessage = (message: { type: string, taskId?: string }, _sender: chrome.runtime.MessageSender, sendResponse: (response?: unknown) => void) => {
            if (message.type === 'EXTENSION_CONNECTED' && !extensionConnectedRef.current) {
                extensionConnectedRef.current = true;

                // Respond with current state info
                sendResponse({
                    success: true,
                    isIframe: isInIframe(),
                    ready: ready,
                    taskId: store.getState().tasks.selectedTaskId
                });

                return true;
            }

            if (message.type === 'UPDATE_TASK_ID' && message?.taskId) {
                dispatch(setSelectedTaskId(message.taskId));
                sendResponse({ success: true });
                return true;
            }

            if (message.type === 'OPEN_SITE_SELECTION_MODAL') {
                dispatch(setShowSiteSelectionModal(true));
                sendResponse({ success: true });
                return true;
            }

            return false;
        };

        // Add listener for extension messages
        if (chrome.runtime && chrome.runtime.onMessage) {
            chrome.runtime.onMessage.addListener(handleExtensionMessage);

            // Notify extension that content script is ready
            if (!extensionConnectedRef.current) {
                try {
                    chrome.runtime.sendMessage({
                        type: 'CONTENT_SCRIPT_READY',
                        isIframe: isInIframe(),
                        url: window.location.href
                    });
                } catch (error) {
                    console.error('Failed to send ready message to extension:', error);
                }
            }

            return () => {
                if (chrome.runtime && chrome.runtime.onMessage) {
                    chrome.runtime.onMessage.removeListener(handleExtensionMessage);
                }
            };
        }
    }, [dispatch, ready]);

    // Notify parent about task ID changes
    useEffect(() => {
        const notifyParentAboutTaskIdChange = (taskId: string) => {
            if (isInIframe() && window.parent) {
                window.parent.postMessage({
                    type: 'IFRAME_TASK_ID_CHANGED',
                    taskId: taskId
                }, '*');
            }

            // Chrome extension specific - notify background script about task ID change
            if (isChromeExtension()) {
                try {
                    chrome.runtime.sendMessage({
                        type: 'TASK_ID_CHANGED',
                        taskId: taskId,
                        isIframe: isInIframe(),
                        url: window.location.href
                    });
                } catch (error) {
                    console.error('Failed to send task ID change to extension:', error);
                }
            }
        };

        const unsubscribe = store.subscribe(() => {
            const state = store.getState();
            const currentTaskId = state.tasks.selectedTaskId;

            if (currentTaskId && currentTaskId !== previousTaskIdRef.current) {
                previousTaskIdRef.current = currentTaskId;
                notifyParentAboutTaskIdChange(currentTaskId);
            }
        });

        return () => unsubscribe();
    }, []);

    // Handle display mode changes
    useEffect(() => {
        if (displayMode !== 'desktop') {
            window.scrollTo({
                top: 0,
                behavior: 'auto'
            });

            setTimeout(() => {
                document.body.style.overflow = 'hidden';
            }, 10);
        } else {
            document.body.style.overflow = 'auto';
        }

        // Chrome extension specific - notify about display mode change
        if (isChromeExtension()) {
            try {
                chrome.runtime.sendMessage({
                    type: 'DISPLAY_MODE_CHANGED',
                    displayMode: displayMode
                });
            } catch (_) {
                // Silent error - extension might not be available
            }
        }
    }, [displayMode]);

    useEffect(() => {
        if (userData.name === '' && userData.email === '') return
        if (userData?.name === userData?.email && userData) {
            if (userData.role === 'collaborator') {
                handleUpdateUserProfile()
                return
            }
            dispatch(setShowUpdateUserName(true));
        }
    }, [userData, dispatch, handleUpdateUserProfile]);

    useEffect(() => {
        if (
            userData?.role === 'admin' &&
            userData?.raw?.trial?.status === 'inactive'
        ) {
            if (isInIframe() && window.parent) {
                // In iframe mode, send message to parent
                if (userData?.raw?.trial?.company_name === null && userData?.company_name === null) {
                    dispatch(setShowCompanyDetail({ open: true, companyName: null }));
                    dispatch(setSignupOption('regular'))
                    return
                } else {
                    window.parent.postMessage({
                        type: 'START_WIZARD',
                        userData: userData.raw
                    }, '*');
                }
            }
        } else if (userData?.role === 'collaborator' && userData?.raw?.trial?.status === 'inactive') {
            // if (register) {
            //     dispatch(setShowForgotPassword(false))
            //     dispatch(setShowLogin(false))
            //     dispatch(setShowRegister(false))
            //     dispatch(setHelpModalOpen(true))
            // }
            // dispatch(setShowForgotPassword(false))
            // dispatch(setShowLogin(false))
            // dispatch(setShowRegister(false))
        }
    }, [userData, dispatch, register, userData?.company_name]);


    const godaddy_invite_token = localStorage.getItem('atarimGuestUser');
    useEffect(() => {
        if (!godaddy_invite_token &&
            (collaborateInfo?.result && enterpriseClients.includes(collaborateInfo.result.account_id)) &&
            ((!userData?.name || userData.name.trim() === '') && collaborateInfo?.result?.role === 'Guest')) {
            const guestData: Partial<UserResponse['data']> = {
                ...userData,
                // Normalize potentially null fields to undefined to satisfy strict types
                first_name: userData?.first_name ?? undefined,
                last_name: userData?.last_name ?? undefined,
                username: userData?.username ?? undefined,
                name: collaborateInfo.result?.godaddy_username || 'Guest',
                role: 'Guest',
                id: collaborateInfo?.result.user_id,
                isGuest: false,
                isGodaddyGuest: true,
            }
            // Store guest data in localStorage
            window.localStorage.setItem('atarimGuestUser', JSON.stringify(guestData));
            dispatch(setLoggedIn(true))
            dispatch(setShowRegister(false))
            dispatch(setUserDetails({ data: guestData as UserResponse['data'], status: true }))
            dispatch(setOpenGodaddyGuestNameModal(true))
        }
    }, [collaborateInfo, dispatch, godaddy_invite_token, userData]);

    // Plugin activation redirect flow
    useEffect(() => {
        if (isInIframe()) return
        const activationCallback = new URL(window.location.toString()).searchParams.get('activation_callback');
        if (!activationCallback) return
        const urlUpdate = new URL(window.location.href);
        urlUpdate.searchParams.delete('collab');
        window.history.replaceState({}, '', urlUpdate.toString());
        if (userData?.loggedIn && userData?.raw) {
            handlePluginActivation()
            return;
        }
        if (register || forgotPassword || userData?.company_name) return
        if (isAuthenticationFailed) {
            dispatch(setShowLogin(true))
        }
    }, [userData, login, register, forgotPassword, dispatch, handlePluginActivation, isAuthenticationFailed]);


};